import { ReportingPeriod, TimeRange } from '@nirvana/shared/dtos/types/shared/time';
import { FeatureFlag } from '@nirvana/shared/constants';

export enum Status {
  ACTIVE = 0,
  NO_COMPANIES_IN_TARGETS = 1,
}

export enum DashboardType {
  CUSTOM = 0,
  EXECUTIVE_SUMMARY = 1,
}

export interface Position {
  x: number;
  y: number;
}

export enum CanExportPdf {
  HIDDEN = 0,
  VISIBLE_BUT_DISABLED = 1,
  VISIBLE = 2,
}

export interface Features {
  canExportPdf?: CanExportPdf;
}

export interface Dashboard {
  id: string;
  name: string;
  type: DashboardType;
  timeRange: TimeRange;
  widgets?: WidgetWithId[];
  created?: Date;
  lastModified?: Date;
  targets: Array<string>;
  canDrillDown?: boolean;
  features?: Features;
}

export enum WidgetType {
  OVERVIEW = 0,
  TOP_TYPES_THREATS_BLOCKED = 1,
  THREATS_BREAKDOWN = 2,
  INCIDENTS_STATUS = 3,
  REMEDIATION_ACTIONS = 4,
  INVENTORY = 5,
  ACCESS_DATA_PROTECTION = 6,
  ENDPOINT_MODULES_STATUS = 7,
  COMPANY_RISK_SCORE = 8,
  WEBSITES_BLOCKED = 9,
  NETWORK_ATTACK_TECHNIQUES = 10,
  ENDPOINT_MODULES_STATUS_DETAILS = 11,
}

export interface WidgetWithId {
  id: string;
  type: WidgetType;
  pos?: Position;
  width: number;
  height: number;
}

export interface Target {
  path?: string;
  companyId: string;
  companyName: string;
}

export interface GetExecutiveSummaryPdfRequest {
  dashboard: Dashboard;
  dashboardToken: string;
  widgetsResult: Array<WidgetResult>;
  locale: string;
  timezone: string;
  reportingPeriod: ReportingPeriod;
  selectedTarget: Target;
  userCompanyId?: string;
}

export interface IncidentsDistributionBySeverity {
  highSeverity?: number;
  mediumSeverity?: number;
  lowSeverity?: number;
}

export interface MonitoredResources {
  files?: number;
  domains?: number;
  registries?: number;
  ips?: number;
  processes?: number;
}

export interface EdrTrialReportArrayValue {
  name: string;
  value: number;
}

export interface EndpointsData {
  endpointsWithEdr: number;
  suspiciousIncidents: number;
  correlatedIncidents: number;
  incidentsDistributionBySeverity?: IncidentsDistributionBySeverity;
  monitoredResources?: MonitoredResources;
  topAffectedDevices?: Array<EdrTrialReportArrayValue>;
}

export interface ExploitedResources {
  files?: number;
  processes?: number;
  registries?: number;
  ips?: number;
  domains?: number;
}

export interface ThreatsData {
  exploitedResources?: ExploitedResources;
  mitreAttacks?: Array<EdrTrialReportArrayValue>;
  threatFamilies?: Array<EdrTrialReportArrayValue>;
  actors?: Array<EdrTrialReportArrayValue>;
}

export interface EdrTrialReportingPeriod {
  startDate: number;
  endDate: number;
}

export interface EdrTrialReportData {
  companyName: string;
  edrTrialReportingPeriod: EdrTrialReportingPeriod;
  companyLogo: string;
  distributorName: string;
  country: string;
  industry: string;
  endpointsData: EndpointsData;
  threatsData: ThreatsData;
}

export interface GetEdrTrialReportPdfRequest {
  data: EdrTrialReportData;
  locale: string;
  timezone: string;
}

export interface GetIncidentReportPdfRequest {
  incidentId: string;
}

export interface WidgetResult {
  widgetId: string;
  widgetResultJson: string;
}

export interface GetExecutiveSummaryPdfResponse {
  pdf: Uint8Array;
}

export interface GetEdrTrialReportPdfResponse {
  pdf: Uint8Array;
}

export interface GetIncidentReportPdfResponse {
  pdf: Uint8Array;
  name: string;
}
